import React, { useEffect, useState } from "react";
import "./ManagerCheckListView.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { beforeVerficationDocuments, verficationFetchSubmitedResponse, verificationReview } from "../../../../services/checkListService";
import { getAuditLog } from "../../../../utils/calculateDaysInMonthUtils";
import { getFileName } from "../../../../utils/validations";
const ManagerCheckListView = () => {
    const navigate = useNavigate();
    const [educationData, setEducationData] = useState([]);
    const [experienceData, setExperienceData] = useState([]);
    const [bankData, setBankData] = useState([]);
    const [idDetailsData, setIdDetailsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [responseData, setResponseData] = useState([]);
    const [error, setError] = useState(null);
    const [fullname, setFullname] = useState("");
    const [jobPosition, setJobPosition] = useState("");
    const [approvalComment, setApprovalComment] = useState("");
    const location = useLocation();
    const [onboardingDate, setOnboardingDate] = useState("");
    const [remarkError, setRemarkError] = useState("");

    const employeeUniqueId = location.state?.application?.personalInformation?.employeeUniqueId;

    // === Fetch managerChecklist data from API ===
    const fetchCheckList = async () => {
        try {
            // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/documents/${employeeUniqueId}`);
            const response = await beforeVerficationDocuments(employeeUniqueId);
            const { employeeEducation, employeeExperience, bankDetails, employeeIDDetails } = response.data;

            // Normalize Education Data
            const education = (employeeEducation || []).map((item) => ({
                id: item.id,
                type: "Education",
                qualification: item.qualification?.qualificationName || "-",
                fileName: item.certificate,
                fileUrl: item.certificate,
                status: "",
                comment: "",
            }));

            // Normalize Experience Data
            const experience = (employeeExperience || []).map((item) => ({
                id: item.id,
                type: "Experience",
                qualification: item.designationName || "-",
                fileName: item.relievingLetter,
                fileUrl: item.relievingLetter,
                status: "",
                comment: "",
            }));

            // Normalize Bank Details Data
            const bank = bankDetails
                ? [{
                    id: bankDetails.id,
                    type: "Bank Details",
                    qualification: bankDetails.accountType?.accountTypeName || "-",
                    fileName: bankDetails.passbookFileName,
                    fileUrl: bankDetails.passbookFileName,
                    status: "",
                    comment: "",
                }]
                : [];

            // Normalize ID Details Data
            const idDetails = employeeIDDetails
                ? [
                    {
                        id: `${employeeIDDetails.id}-aadhaar`,
                        type: "ID Details",
                        qualification: "Aadhaar Card",
                        fileName: employeeIDDetails.aadhaarFileName,
                        fileUrl: employeeIDDetails.aadhaarFileName,
                        status: "",
                        comment: "",
                        field: "aadhaar",
                    },
                    {
                        id: `${employeeIDDetails.id}-pan`,
                        type: "ID Details",
                        qualification: "PAN Card",
                        fileName: employeeIDDetails.panCardFileName,
                        fileUrl: employeeIDDetails.panCardFileName,
                        status: "",
                        comment: "",
                        field: "pan",
                    },
                    employeeIDDetails.hasUanNumber && {
                        id: `${employeeIDDetails.id}-uan`,
                        type: "ID Details",
                        qualification: "UAN Card",
                        fileName: employeeIDDetails.uanCardFileName,
                        fileUrl: employeeIDDetails.uanCardFileName,
                        status: "",
                        comment: "",
                        field: "uan",
                    },
                    employeeIDDetails.hasEsiNumber && {
                        id: `${employeeIDDetails.id}-esi`,
                        type: "ID Details",
                        qualification: "ESI Card",
                        fileName: employeeIDDetails.esiCardFileName,
                        fileUrl: employeeIDDetails.esiCardFileName,
                        status: "",
                        comment: "",
                        field: "esi",
                    },
                    {
                        id: `${employeeIDDetails.id}-passport`,
                        type: "ID Details",
                        qualification: "Passport Size Photo",
                        fileName: employeeIDDetails.employeepassportSizeFileName,
                        fileUrl: employeeIDDetails.employeepassportSizeFileName,
                        status: "",
                        comment: "",
                        field: "photo",
                    },
                ].filter(Boolean)
                : [];

            setFullname(response.data.fullname || "");
            setJobPosition(response.data.jobPosition || "");
            setEducationData(education);
            setExperienceData(experience);
            setBankData(bank);
            setIdDetailsData(idDetails);
        } catch (err) {
            console.error("Error fetching managerChecklist:", err);
            setError("Failed to fetch managerChecklist");
        } finally {
            setLoading(false);
        }
    };

    const fetchSubmitedResponse = async () => {
        try {
            // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/verification/verification-status/${employeeUniqueId}`);
            // const data = response.data;
            const data = await verficationFetchSubmitedResponse(employeeUniqueId);
            setResponseData(data);

            // === Update education data with verification status ===
            setEducationData(prev =>
                prev.map(doc => {
                    const verifiedDoc = data.educationVerifications?.find(v => v.documentId === doc.id);
                    return verifiedDoc
                        ? {
                            ...doc,
                            verifiedStatus: verifiedDoc.verified ? "VERIFIED" : "RESUBMIT", // Use separate field for verification status
                            comment: verifiedDoc.comment || "",
                        }
                        : doc;
                })
            );

            // === Update experience data with verification status ===
            setExperienceData(prev =>
                prev.map(doc => {
                    const verifiedDoc = data.experienceVerifications?.find(v => v.documentId === doc.id);
                    return verifiedDoc
                        ? {
                            ...doc,
                            verifiedStatus: verifiedDoc.verified ? "VERIFIED" : "RESUBMIT",
                            comment: verifiedDoc.comment || "",
                        }
                        : doc;
                })
            );

            // === Update bank data ===
            setBankData(prev =>
                prev.map(doc => ({
                    ...doc,
                    verifiedStatus: data.bankDetailsVerified ? "VERIFIED" : "RESUBMIT",
                    comment: data.bankDetailsComment || "",
                }))
            );

            // === Update ID details ===
            setIdDetailsData(prev =>
                prev.map(doc => {
                    const field = doc.field;
                    const verified = data[`${field}Verified`];
                    const comment = data[`${field}Comment`] || "";
                    return {
                        ...doc,
                        verifiedStatus: verified ? "VERIFIED" : "RESUBMIT",
                        comment,
                    };
                })
            );
        } catch (error) {
            console.log("Error fetching verification response:", error);
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            await fetchCheckList(); // Load document data
            await fetchSubmitedResponse(); // Then load verification status
        };
        fetchAllData();
    }, []);
    const areAllDocumentsApproved = () => {
        const allDocs = [
            ...educationData,
          //  ...experienceData,
            ...bankData,
            ...idDetailsData
        ];
        return allDocs.every(doc => doc.status === "APPROVED");
    };
    const handleCheckboxChange = (id, status, setDataFunction) => {
        setDataFunction((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, status: row.status === status ? "" : status } : row
            )
        );
    };

    const handleCommentChange = (id, value, setDataFunction) => {
        setDataFunction((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, comment: value } : row
            )
        );
    };

    // const getFileName = (url) => {
    //     if (!url) return "-";
    //     const fileName = url.split("/").pop();
    //     const lastUnderscore = fileName.lastIndexOf("_");
    //     return lastUnderscore !== -1 ? fileName.substring(lastUnderscore + 1) : fileName;
    // };

    const handleSubmit = async () => {
        const fullName = localStorage.getItem("full_name");
        const role = localStorage.getItem("role");
        const employeeId = localStorage.getItem("employeeId");

        // === Determine finalApproved ===
        const allApproved = [
            ...educationData,
            ...experienceData,
            ...bankData,
            ...idDetailsData
        ].every(doc => doc.status === "APPROVED");

        // === Payload for /api/verification/review ===
        const reviewPayload = {
            employeeUniqueId,
            managerReviewed: true, // Set to true by default
            finalApproved: allApproved,
            approvalComment: approvalComment,
            onboardingDate: onboardingDate,
            // auditLog: {
            //     name: fullName,
            //     role,
            //     employeeId,
            // },
            auditLog: getAuditLog(),
        };

        console.log("Submitting review payload:", reviewPayload);

        try {
            // Submit to /api/verification/review
            // await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/verification/review`,reviewPayload);
            await verificationReview(reviewPayload);
            alert("Review submitted successfully!");
            navigate("/employeeCheckListVerification");
        } catch (err) {
            console.error("Error submitting review:", err);
            alert("Failed to submit review");
        }
    };

    // Remove top-level allDocs/anyMissingApproval/etc. variables

    // Handle Reject action with dynamic remark validation
    const handleReject = () => {
        const allDocs = [...educationData, ...experienceData, ...bankData, ...idDetailsData];
        const anyMissingApproval = allDocs.some(doc => doc.status !== "APPROVED");
        const isRemarkEmpty = !approvalComment.trim();

        if (anyMissingApproval && isRemarkEmpty) {
            setRemarkError("Kindly enter Remarks");
            return; // Stop submission
        }

        setRemarkError(""); // Clear error if remark exists
        handleSubmit();
    };

    const isRejectDisabled = () => {
        const allDocs = [...educationData, ...experienceData, ...bankData, ...idDetailsData];
        return allDocs.every(doc => doc.status === "APPROVED"); // disable reject only if all approved
    };

    // Clear error message as soon as user types something
    useEffect(() => {
        if (approvalComment.trim() !== "") {
            setRemarkError("");
        }
    }, [approvalComment]);




    const renderTable = (data, setDataFunction, title, customHeader) => (
        <div className="table-section">
            <h3>{title}</h3>
            <div className="table-view-managerChecklist">
                <div className="table-header-managerChecklist">
                    <div>S.No</div>
                    <div>{customHeader || "Document"}</div>
                    <div>File Name</div>
                    <div>File</div>
                    <div>Verified</div>
                    <div>Approved</div>
                </div>
                {data.length > 0 ? (
                    data.map((row, index) => {
                        const isExperienceRow = row.type === "Experience";
                        const isFileMissing = !row.fileName || row.fileName === "null" || row.fileName === null;
                        const isCompanyEmpty = !row.qualification || row.qualification === "-";
                        const isFileMissingAndEmptyCompany = isExperienceRow && isFileMissing && isCompanyEmpty;

                        return (
                            <div
                                key={row.id}
                                className={`table-row-managerChecklist ${row.status?.toLowerCase() || ""}`}
                            >
                                <div>{index + 1}</div>
                                <div>{row.qualification || "-"}</div>
                                <div>{getFileName(row.fileName)}</div>
                                <div>
                                    {row.fileUrl ? (
                                        <a
                                            href={
                                                row.fileUrl.startsWith("http")
                                                    ? row.fileUrl
                                                    : `${process.env.REACT_APP_FILE_BASE_URL}/${row.fileUrl}`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            View
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                                <div>
                                    {isFileMissingAndEmptyCompany ? (
                                        "-"
                                    ) : (
                                        <input
                                            type="checkbox"
                                            checked={row.verifiedStatus === "VERIFIED"}
                                            disabled // Disable the Verified checkbox
                                        />
                                    )}
                                </div>
                                <div>
                                    {isFileMissingAndEmptyCompany ? (
                                        "-"
                                    ) : (
                                        <input
                                            type="checkbox"
                                            checked={row.status === "APPROVED"}
                                            onChange={() =>
                                                handleCheckboxChange(row.id, "APPROVED", setDataFunction)
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="table-row-managerChecklist">No data available</div>
                )}
            </div>
        </div>
    );

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    return (
        <div className="managerChecklist-container-view">
            <h2 className="title">Manager Verification Check List</h2>
            <div>
                <span className="emp-details">Employee details:-</span> &nbsp;
                <span>{employeeUniqueId}&nbsp;-&nbsp;{fullname}&nbsp;-&nbsp;{jobPosition}</span>
            </div>
            {renderTable(educationData, setEducationData, "Education Documents")}
            {renderTable(experienceData, setExperienceData, "Experience Documents")}
            {renderTable(bankData, setBankData, "Bank Details", "Document")}
            {renderTable(idDetailsData, setIdDetailsData, "ID Details")}
            <div className="form-container-view">
                <div className="remark-section">
                    <label htmlFor="approvalComment">Remark:</label>
                    <input
                        type="text"
                        id="approvalComment"
                        value={approvalComment}
                        onChange={(e) => setApprovalComment(e.target.value)}
                        placeholder="Enter remark (e.g., Approved for onboarding)"
                        className="remark-input"
                    />
                    {remarkError && <p className="error-text">{remarkError}</p>}

                </div>

                <div className="date-section">
                    {/* <label htmlFor="onboardingDate">Onboarding Date:</label>
                    <input
                        type="date"
                        id="onboardingDate"
                        className="date-input"
                        value={onboardingDate}
                        onChange={(e) => setOnboardingDate(e.target.value)} // Capture the date here
                    /> */}

                </div>
            </div>
            <div className="buttons-flow">
                <button
                    className="submit-btn-verfication-mg-check"
                    onClick={handleSubmit}
                    disabled={!areAllDocumentsApproved()} // Disable if not all are approved
                >
                    Submit Verification
                </button>
                <button
                    className="submit-btn-reject-mg-check"
                    onClick={handleReject}
                    disabled={isRejectDisabled()} // Call function here // Disable if all are approved
                >
                    Reject Verification
                </button>
            </div>

        </div>
    );
};

export default ManagerCheckListView;